import { useState, useContext, useEffect } from 'react'
import SectionDetailView from '../ai-training/SectionDetailView'
import { AuditConfigContext } from '../../../global/context/audit-config-context/AuditConfigContext'
import { STATUS } from '../../../global/types/audit'
import { LOCAL_STORAGE_KEYS } from '../../../global/constants/local-storage'
import {
  CLEANLINESS_PROMPT_SECTION_KEY,
  CLEANLINESS_PROMPT_SECTION_TITLE,
} from '../../../global/constants/cleanliness'
import styles from './AITrainingTable.module.css'

// AuditType is just an alias for STATUS
export type AuditType = STATUS

function AITrainingTable() {
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [selectedAuditType, setSelectedAuditType] = useState<AuditType | null>(
    null,
  )

  const { configs, loading, error } = useContext(AuditConfigContext)

  // Load last selected audit type and section from localStorage on mount
  useEffect(() => {
    const savedAuditType = localStorage.getItem(
      LOCAL_STORAGE_KEYS.AI_TRAINING.LAST_AUDIT_TYPE,
    ) as AuditType | null
    const savedSection = localStorage.getItem(LOCAL_STORAGE_KEYS.AI_TRAINING.LAST_SECTION)

    if (savedAuditType) {
      setSelectedAuditType(savedAuditType)
    }
    if (savedSection) {
      setSelectedSection(savedSection)
    }
  }, [])

  // Save selections to localStorage whenever they change
  useEffect(() => {
    if (selectedAuditType) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.AI_TRAINING.LAST_AUDIT_TYPE, selectedAuditType)
    }
  }, [selectedAuditType])

  useEffect(() => {
    if (selectedSection) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.AI_TRAINING.LAST_SECTION, selectedSection)
    }
  }, [selectedSection])

  // Helper to get nice display label for audit type
  const getAuditTypeLabel = (auditType: STATUS): string => {
    const labelMapping: Record<STATUS, string> = {
      [STATUS.DAILY_DVIR]: 'Daily DVIR',
      [STATUS.WEEKLY_DVIR]: 'Weekly DVIR',
      [STATUS.COMPLETED_INSTALLATION]: 'Installation',
      [STATUS.ON_SITE_CANCELLATION]: 'On-Site Cancellation',
    }
    return labelMapping[auditType] || auditType
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <h2>Error Loading Audit Configs</h2>
          <p>{error}</p>
        </div>
      </div>
    )
  }

  // Get sections for the selected audit type
  const getSectionsForAuditType = (auditType: AuditType | null) => {
    if (!auditType) return []
    const sections = Object.keys(configs[auditType].SECTIONS)
      .filter((key) => key !== 'completed')
      .map((key) => ({
        key,
        title: configs[auditType].TITLES[key] || key,
      }))
    // The van cleanliness rubric (FRON-1601) is edited here too. It reads the
    // weekly DVIR interior photos but is not an audit section of its own.
    if (auditType === STATUS.WEEKLY_DVIR) {
      sections.push({
        key: CLEANLINESS_PROMPT_SECTION_KEY,
        title: CLEANLINESS_PROMPT_SECTION_TITLE,
      })
    }
    return sections
  }

  // Generate audit types dynamically from configs
  const auditTypes: { key: AuditType; label: string }[] = Object.keys(
    configs,
  ).map((key) => ({
    key: key as STATUS,
    label: getAuditTypeLabel(key as STATUS),
  }))

  const sections = getSectionsForAuditType(selectedAuditType)

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {loading ? (
          <div className={styles.emptyState}>
            <h2>Loading...</h2>
            <p>Fetching audit configurations from the backend...</p>
          </div>
        ) : (
          <SectionDetailView
            auditType={selectedAuditType}
            sectionKey={selectedSection}
            config={selectedAuditType ? configs[selectedAuditType] : null}
            auditTypes={auditTypes}
            sections={sections}
            onAuditTypeChange={(newType: AuditType) => {
              setSelectedAuditType(newType)
              setSelectedSection(null)
            }}
            onSectionChange={(newSection: string) =>
              setSelectedSection(newSection)
            }
          />
        )}
      </div>
    </div>
  )
}

export default AITrainingTable
